import { issueDifficultySchema } from "~/lib/difficulty"
import { z } from "zod"
import {
	ContentTemplateKind,
	IssuePriority,
	IssueStatus,
	ProjectStatus
} from "~/lib/enums"
import { isReserved } from "./reserved-slugs"

export const organizationSchema = z.object({
	name: z
		.string()
		.min(1, { message: "Organization name is required" })
		.max(24, { message: "Organization name must be 24 characters or less" }),
	slug: z
		.string()
		.min(3, { message: "Organization slug must be at least 3 characters long" })
		.max(12, { message: "Organization slug must be 12 characters or less" })
		.regex(/^[a-z0-9-]+$/, {
			message: "Slug can only contain lowercase letters, numbers, and dashes"
		})
		.refine(async (slug) => !isReserved(slug), {
			message: "Slug is not available"
		}),
	logo: z.string().url().optional(),
	discordAuditLogWebhook: z
		.string()
		.regex(/^https:\/\/discord\.com\/api\/webhooks\/.+$/, {
			message: "Audit log webhook must be a Discord webhook URL"
		})
		.optional()
		.or(z.literal(""))
})

export const teamOnboardingSchema = z.object({
	name: z
		.string()
		.min(1, { message: "Team name is required" })
		.max(24, { message: "Team name must be 24 characters or less" }),
	slug: z
		.string()
		.min(3, { message: "Team slug must be at least 3 characters long" })
		.max(12, { message: "Team slug must be 12 characters or less" })
		.regex(/^[a-z0-9-]+$/, {
			message: "Slug can only contain lowercase letters, numbers, and dashes"
		}),
	logo: z.string().url().optional(),
	organizationId: z.string()
})

export const contentTemplateSchema = z.object({
	id: z.string().min(1).optional(),
	teamId: z.string().min(1),
	name: z.string().trim().min(1, "Template name is required").max(100),
	kind: z.enum(ContentTemplateKind),
	content: z.string().min(1).max(100_000),
	updatedAt: z.date().optional()
})

export const projectSchema = z.object({
	id: z.string().min(1).optional(),
	teamId: z.string().min(1),
	name: z.string().trim().min(1, "Project name is required").max(100),
	description: z.string().trim().max(5000),
	status: z.enum(ProjectStatus),
	leadId: z.string().min(1).nullable(),
	startDate: z.date().nullable().optional(),
	targetDate: z.date().nullable(),
	resources: z
		.array(
			z.object({
				title: z.string().trim().min(1).max(100),
				url: z
					.url()
					.max(2000)
					.refine(
						(value) => /^https?:\/\//i.test(value),
						"Use an HTTP or HTTPS link"
					)
			})
		)
		.max(20),
	updatedAt: z.date().optional()
})

export const projectPropertiesSchema = z.object({
	teamId: z.string().min(1),
	id: z.string().min(1),
	updatedAt: z.date(),
	changes: projectSchema
		.pick({ status: true, leadId: true, targetDate: true, startDate: true })
		.partial()
		.refine(
			(value) => Object.keys(value).length > 0,
			"Choose a property to update."
		)
})

export const issueSchema = z.object({
	title: z.string(),
	description: z.string().nullable(),
	assigneeId: z
		.string()
		.optional()
		.nullable()
		.describe("Null = unset, undefined = don't change"),
	labelIds: z.array(z.string()).optional(),
	projectId: z.string().min(1).nullable().optional(),
	status: z.enum(IssueStatus),
	priority: z.enum(IssuePriority),
	difficulty: issueDifficultySchema,
	dueDate: z.date().nullable().optional(),
	teamId: z.string()
})

export const issueEditSchema = issueSchema.partial().extend({
	id: z.string(),
	teamId: z.string(),
	descriptionRevision: z.number().int().nonnegative().optional()
})

export const issueCreateSchema = issueSchema.extend({
	difficulty: issueDifficultySchema.optional()
})

export const bulkIssueUpdateSchema = z.object({
	teamId: z.string(),
	issueIds: z.array(z.string()).min(1).max(100),
	status: z.enum(IssueStatus).optional(),
	priority: z.enum(IssuePriority).optional(),
	difficulty: issueDifficultySchema.optional(),
	dueDate: z.date().nullable().optional(),
	assigneeId: z.string().nullable().optional(),
	labels: z
		.object({
			mode: z.enum(["add", "remove", "replace"]),
			labelIds: z.array(z.string()).max(50)
		})
		.optional()
})

export const teamSchema = z
	.object({
		enableHome: z.boolean().optional(),
		enableIssues: z.boolean().optional(),
		enableAutomations: z.boolean().optional(),
		enableKnowledgeBase: z.boolean().optional(),
		enableSuggestions: z.boolean().optional(),
		enableQueue: z.boolean(),
		enableProjects: z.boolean().optional(),
		enableForms: z.boolean().optional(),
		defaultView: z.enum(["list", "kanban", "calendar"]),
		syncAutomaticallyToGitHub: z.boolean().optional()
	})
	.merge(teamOnboardingSchema)

export const issueCommentSchema = z.object({
	issueId: z.string(),
	comment: z.string(),
	repliesToId: z.string().optional()
})

export const labelSchema = z.object({
	name: z
		.string()
		.trim()
		.min(1, "Enter a label name.")
		.max(100, "Label names must be 100 characters or fewer."),
	color: z.string().regex(/^#[0-9A-Fa-f]{6}$/),
	teamId: z.string()
})

export const roleSchema = z.object({
	name: z
		.string()
		.trim()
		.min(1, "Enter a role name.")
		.max(100, "Role names must be 100 characters or fewer."),
	color: z.string().regex(/^#([0-9a-fA-F]{6})$/, {
		message: "Color must be a valid hex color"
	}),
	permissions: z.number().int().min(0)
})

export const editRoleSchema = roleSchema.partial().extend({
	id: z.string()
})

export const formDiscordChannelSchema = z.object({
	guildId: z.string().regex(/^\d{17,20}$/),
	channelId: z.string().regex(/^\d{17,20}$/)
})

export const formDiscordSettingsSchema = z.object({
	mode: z.enum(["inherit", "off", "channel"]),
	guildId: z.string(),
	channelId: z.string()
})

export const formSchema = z.object({
	id: z.string().min(1).optional(),
	teamId: z.string().min(1),
	name: z.string().trim().min(1, "Form name is required").max(100),
	description: z.string().trim().max(1000),
	updatedAt: z.date().optional()
})

const answerValueSchema = z.union([
	z.string().max(100_000),
	z.number(),
	z.boolean(),
	z.null(),
	z.array(z.string().max(100_000)).max(50),
	z.object({ name: z.string().max(255), url: z.string().max(2000) }),
	z.object({ choice: z.literal("Other"), text: z.string().max(100_000) })
])

export const formAnswerSchema = z.object({
	fieldId: z.string().min(1),
	value: answerValueSchema
})

export const formSubmissionSchema = z.object({
	teamId: z.string().min(1),
	formId: z.string().min(1),
	answers: z.array(formAnswerSchema).max(200)
})
